export type CompetitorSupplier = 'hoteldo' | 'expedia' | 'expedia_taap' | 'litoralverde' | 'outros';

/** Tiers de remuneração do Expedia TAAP (extensível a outros fornecedores no futuro). */
export type RemunerationTier = 'basic' | 'basic_plus' | 'premium' | 'premium_plus';

export interface CompetitorQuote {
  supplier: CompetitorSupplier;
  /** Texto livre (ex.: "R$ 4.200", "USD 890 / noite") */
  price: string;
  description: string;
  /** URL da página do hotel no fornecedor (HotelDo, Expedia TAAP, etc.). */
  supplier_url?: string;
  /** Tipo de remuneração ao agente. Obrigatório para expedia_taap; opcional nos demais. */
  remuneration_tier?: RemunerationTier | string;
}

export const COMPETITOR_SUPPLIER_OPTIONS: { value: CompetitorSupplier; label: string }[] = [
  { value: 'hoteldo', label: 'HotelDo' },
  { value: 'expedia', label: 'Expedia' },
  { value: 'expedia_taap', label: 'Expedia TAAP' },
  { value: 'litoralverde', label: 'Litoralverde' },
  { value: 'outros', label: 'Outros' },
];

export const EXPEDIA_TAAP_TIERS: { value: RemunerationTier; label: string }[] = [
  { value: 'basic', label: 'Basic' },
  { value: 'basic_plus', label: 'Basic Plus' },
  { value: 'premium', label: 'Premium' },
  { value: 'premium_plus', label: 'Premium Plus' },
];

const SUPPLIER_SET = new Set<string>(COMPETITOR_SUPPLIER_OPTIONS.map(o => o.value));

function isCompetitorSupplier(s: string): s is CompetitorSupplier {
  return SUPPLIER_SET.has(s);
}

function quoteFromUnknownRecord(o: Record<string, unknown>): CompetitorQuote | null {
  const supplier = o.supplier;
  if (typeof supplier !== 'string' || !isCompetitorSupplier(supplier)) return null;
  const quote: CompetitorQuote = {
    supplier,
    price: typeof o.price === 'string' ? o.price : '',
    description: typeof o.description === 'string' ? o.description : '',
  };
  if (typeof o.remuneration_tier === 'string' && o.remuneration_tier) {
    quote.remuneration_tier = o.remuneration_tier as RemunerationTier;
  }
  if (typeof o.supplier_url === 'string' && o.supplier_url.trim()) {
    quote.supplier_url = o.supplier_url.trim();
  }
  return quote;
}

/** Lê JSON do banco: array de cotações ou objeto único (legado). */
export function normalizeCompetitorQuotes(raw: unknown): CompetitorQuote[] | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const out: CompetitorQuote[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const q = quoteFromUnknownRecord(item as Record<string, unknown>);
      if (q) out.push(q);
    }
    return out.length ? out : null;
  }
  if (typeof raw === 'object') {
    const q = quoteFromUnknownRecord(raw as Record<string, unknown>);
    return q ? [q] : null;
  }
  return null;
}

export function competitorSupplierLabel(s: CompetitorSupplier): string {
  return COMPETITOR_SUPPLIER_OPTIONS.find(o => o.value === s)?.label ?? s;
}
